// MissionEditorForm.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Utilities
import { buildMissionTaskSummary } from 'missions/taskmodal/missionTaskModalUtils';

// CSS
import './MissionEditorForm.css';
import './MissionModal.css';

// Components
import DatabaseSettingsMissionRow from './DatabaseSettingsMissionRow';
import Button from 'components/button/Button';
import IconPickerButton from 'components/iconpicker/IconPickerButton';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import TextInput from 'components/textinput/TextInput';

export const MISSION_EDITOR_SECTION_ABOUT = 'about';
export const MISSION_EDITOR_SECTION_TASKS = 'tasks';

/**
 * MissionEditorForm
 *
 * Shared form body for creating or editing a Workflow template.
 *
 * @param {'about'|'tasks'|'all'} [section='all'] - Which section to render.
 * @returns {ReactNode} The rendered component.
 */
const MissionEditorForm = ({
  missionDescription = '',
  missionIconKey = 'account_tree',
  missionMetaError = '',
  missionTasks = [],
  missionTitle = '',
  onAddMissionTask,
  onEditMissionTask,
  onMissionDescriptionChange,
  onMissionIconKeyChange,
  onMissionTitleChange,
  onReorderMissionTask,
  section = 'all',
  width = '100%',
}) => {
  const [orderedTasks, setOrderedTasks] = useState([]);
  const [draggingTaskKey, setDraggingTaskKey] = useState('');
  const [dragOverTaskKey, setDragOverTaskKey] = useState('');

  const missionRows = useMemo(() => (
    (Array.isArray(orderedTasks) ? orderedTasks : []).map((task) => ({
      key: String(task?.key || '').trim(),
      label: String(task?.title || '').trim() || 'Untitled step',
      note: buildMissionTaskSummary(task),
      task,
    })).filter((row) => row.key)
  ), [orderedTasks]);

  const hasMissionTitle = Boolean(String(missionTitle || '').trim());
  const showAbout = section === 'all' || section === MISSION_EDITOR_SECTION_ABOUT;
  const showTasks = section === 'all' || section === MISSION_EDITOR_SECTION_TASKS;

  useEffect(() => {
    setOrderedTasks(Array.isArray(missionTasks) ? missionTasks : []);
  }, [missionTasks]);

  const handleDragStart = useCallback((task, event) => {
    const nextTaskKey = String(task?.key || '').trim();
    if (!nextTaskKey) return;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', nextTaskKey);
    setDraggingTaskKey(nextTaskKey);
  }, []);

  const handleDragOver = useCallback((task, event) => {
    const nextTaskKey = String(task?.key || '').trim();
    if (!draggingTaskKey || !nextTaskKey || draggingTaskKey === nextTaskKey) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverTaskKey(nextTaskKey);
  }, [draggingTaskKey]);

  const handleDragEnd = useCallback(() => {
    setDraggingTaskKey('');
    setDragOverTaskKey('');
  }, []);

  const handleDrop = useCallback(async (task, event) => {
    const targetTaskKey = String(task?.key || '').trim();
    if (!draggingTaskKey || !targetTaskKey || draggingTaskKey === targetTaskKey) {
      handleDragEnd();
      return;
    }

    event.preventDefault();

    const previousTasks = orderedTasks;
    const draggedIndex = previousTasks.findIndex((row) => row?.key === draggingTaskKey);
    const targetIndex = previousTasks.findIndex((row) => row?.key === targetTaskKey);
    if (draggedIndex < 0 || targetIndex < 0) {
      handleDragEnd();
      return;
    }

    const nextTasks = [...previousTasks];
    const [movedTask] = nextTasks.splice(draggedIndex, 1);
    nextTasks.splice(targetIndex, 0, movedTask);

    setOrderedTasks(nextTasks);
    handleDragEnd();

    try {
      await onReorderMissionTask?.(draggingTaskKey, targetTaskKey);
    } catch {
      setOrderedTasks(previousTasks);
    }
  }, [draggingTaskKey, handleDragEnd, onReorderMissionTask, orderedTasks]);

  return (
    <ModalForm width={width}>
      {showAbout ? (
        <>
          <ModalField>
            <ModalLabel label="Icon" />
            <ModalHint hint="Shown on Playbook chooser cards for this type." />
            <IconPickerButton
              selectedIconKey={missionIconKey || 'account_tree'}
              title="Choose playbook icon"
              onSelect={(nextIconKey) => onMissionIconKeyChange?.(String(nextIconKey || '').trim() || 'account_tree')}
            />
          </ModalField>

          <ModalField>
            <ModalLabel label="Playbook title" />
            <ModalHint hint="Shown on the node Playbook tab and in the type settings list." />

            <TextInput
              error={Boolean(missionMetaError)}
              placeholder="Kitchen & Bath"
              value={missionTitle}
              onChange={(event) => onMissionTitleChange?.(String(event.target.value || ''))}
            />
          </ModalField>

          <ModalField>
            <ModalLabel label="Description" />
            <ModalHint hint="Shown on the Playbook chooser card before launch." />

            <TextInput
              expandable
              multiline
              placeholder="Cabinetry process from first call through install."
              value={missionDescription}
              onChange={(event) => onMissionDescriptionChange?.(String(event.target.value || ''))}
            />
          </ModalField>
        </>
      ) : null}

      {showTasks ? (
        <div className="mission-modal-task-panel">
          <div className="mission-modal-task-panel-header">
            <div className="mission-modal-task-panel-header-left">
              <ModalLabel label="Steps" />
              <ModalHint hint="List order sets the default next task for Complete on each task. Drag to reorder." />
            </div>

            <div className="mission-modal-task-panel-button">
              <Button
                enabled={hasMissionTitle}
                iconKey="add"
                label="Add Task"
                onClick={() => onAddMissionTask?.()}
              />
            </div>
          </div>

          {missionRows.length > 0 ? (
            <div className="mission-modal-task-list">
              {missionRows.map((row) => (
                <DatabaseSettingsMissionRow
                  key={row.key}
                  isDragging={draggingTaskKey === row.key}
                  isDropTarget={dragOverTaskKey === row.key}
                  label={row.label}
                  note={row.note}
                  onClick={() => onEditMissionTask?.(row.task)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(event) => handleDragOver(row.task, event)}
                  onDragStart={(event) => handleDragStart(row.task, event)}
                  onDrop={(event) => handleDrop(row.task, event)}
                  onSettingsClick={() => onEditMissionTask?.(row.task)}
                />
              ))}
            </div>
          ) : (
            <div className="mission-modal-empty-state">
              {hasMissionTitle
                ? 'No steps yet.'
                : 'Add a playbook title before creating steps.'}
            </div>
          )}
        </div>
      ) : null}
    </ModalForm>
  );
};

MissionEditorForm.displayName = 'MissionEditorForm';

export default MissionEditorForm;
